<template>
  <div class="max-w-5xl mx-auto pb-10 relative">

    <div
      class="space-y-6 transition-all duration-300 animate-fade-in"
      :class="{ 'blur-sm pointer-events-none opacity-50': showModal }"
    >

      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Bodegas & Ubicaciones</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm">Gestiona dónde se almacena físicamente el inventario.</p>
        </div>
        <button
          @click="openCreate"
          class="flex items-center gap-2 px-5 py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl shadow-lg shadow-brand-500/30 transition-all active:scale-95"
        >
          <Plus class="w-5 h-5" /> Nueva Bodega
        </button>
      </div>

      <div class="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
        <div class="relative flex-1 max-w-md">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Buscar por nombre, código o sucursal..."
            class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
          >
        </div>
      </div>

      <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
              <tr>
                <th class="px-6 py-4 font-semibold">Código / Nombre</th>
                <th class="px-6 py-4 font-semibold">Sucursal (Padre)</th>
                <th class="px-6 py-4 font-semibold">Tipo</th>
                <th class="px-6 py-4 font-semibold">Estado</th>
                <th class="px-6 py-4 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">

              <tr v-if="isLoading">
                <td colspan="5" class="px-6 py-8 text-center text-slate-500">Cargando ubicaciones...</td>
              </tr>

              <tr v-else-if="filteredList.length === 0">
                <td colspan="5" class="px-6 py-8 text-center text-slate-500">No se encontraron bodegas.</td>
              </tr>

              <tr v-else v-for="item in filteredList" :key="item.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">

                <td class="px-6 py-4">
                  <div>
                    <div class="font-bold text-slate-900 dark:text-slate-100">{{ item.name }}</div>
                    <div class="text-xs font-mono text-slate-500 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded w-fit mt-1">
                      {{ item.code || '---' }}
                    </div>
                  </div>
                </td>

                <td class="px-6 py-4">
                  <div class="flex items-center gap-2">
                    <Building class="w-4 h-4 text-slate-400" />
                    <span class="text-slate-700 dark:text-slate-300 font-medium">
                      {{ branchesMap[item.branch] || 'Sucursal Desconocida' }}
                    </span>
                  </div>
                </td>

                <td class="px-6 py-4">
                  <span v-if="Number(item.is_saleable)" class="text-xs font-medium text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1 rounded-md">
                    Venta (POS)
                  </span>
                  <span v-else class="text-xs font-medium text-orange-600 bg-orange-50 dark:bg-orange-900/20 px-2 py-1 rounded-md">
                    Almacenaje
                  </span>
                </td>

                <td class="px-6 py-4">
                  <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
                    :class="Number(item.is_active)
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800'
                      : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'">
                    <span class="w-1.5 h-1.5 rounded-full" :class="Number(item.is_active) ? 'bg-emerald-500' : 'bg-slate-400'"></span>
                    {{ Number(item.is_active) ? 'Activa' : 'Inactiva' }}
                  </span>
                </td>

                <td class="px-6 py-4 text-right">
                  <div class="flex justify-end gap-2">
                    <button
                      @click="openEdit(item)"
                      class="p-2 text-slate-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                    >
                      <Edit class="w-4 h-4" />
                    </button>
                    <button
                      @click="deleteItem(item)"
                      class="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 class="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="px-6 py-4 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-500 flex justify-between items-center">
          <span>Mostrando {{ filteredList.length }} bodegas</span>
        </div>
      </div>
    </div>

    <LocationForm
      :show="showModal"
      :locationToEdit="selectedItem"
      @close="showModal = false"
      @saved="loadData"
    />

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { Search, Plus, Edit, Trash2, Building } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import Swal from 'sweetalert2';
import LocationForm from './Form.vue';
import { useToast } from '@/composables/useToast';

const list = ref([]);
const branchesMap = ref({});
const isLoading = ref(true);
const searchTerm = ref('');
const showModal = ref(false);
const selectedItem = ref(null);
const { showSuccess, showError } = useToast();

const loadData = async () => {
  isLoading.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');
    const companyId = user?.company;

    // Nota: Corregí el endpoint a /locations para coincidir con tu backend
    const [resLocs, resBranches] = await Promise.all([
      apiClient.get(`/inventory_locations?company=${companyId}`),
      apiClient.get(`/branches?company=${companyId}`)
    ]);

    const locationsData = Array.isArray(resLocs.data) ? resLocs.data : (resLocs.data.data || []);
    const branchesData = Array.isArray(resBranches.data) ? resBranches.data : (resBranches.data.data || []);

    branchesData.forEach(b => branchesMap.value[b.id] = b.name);
    list.value = locationsData;

  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadData);

const filteredList = computed(() => {
  if (!searchTerm.value) return list.value;
  const term = searchTerm.value.toLowerCase();
  return list.value.filter(i =>
    i.name.toLowerCase().includes(term) ||
    i.code?.toLowerCase().includes(term) ||
    branchesMap.value[i.branch]?.toLowerCase().includes(term)
  );
});

const openCreate = () => { selectedItem.value = null; showModal.value = true; };
const openEdit = (item) => { selectedItem.value = item; showModal.value = true; };

const deleteItem = (item) => {
  Swal.fire({
    title: '¿Eliminar bodega?',
    text: "Esta acción es irreversible si no tiene movimientos.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  }).then(async (res) => {
    if (res.isConfirmed) {
      try {
        await apiClient.delete(`/inventory_locations/${item.id}`);
        list.value = list.value.filter(i => i.id !== item.id);
        showSuccess('Bodega eliminada correctamente.');
      } catch (e) {
        showError('No se pudo eliminar (posiblemente tenga stock).');
      }
    }
  });
};
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fade-in 0.3s ease-out forwards;
}
</style>